import React from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'
import { formatPrice } from '../../utils'
import StronaDokumentu, { NaglowekSekcji } from './StronaDokumentu'

/**
 * Ostatnia strona dokumentu: cena końcowa instalacji i co ona obejmuje.
 *
 * BEZ ROZBICIA NA POZYCJE — i to jest cały sens tej strony. Dokument idzie do
 * klienta końcowego jako załącznik do oferty; rozpisane ceny zakupu sprzętu
 * pokazywałyby mu marżę firmy wprost. Lista poniżej mówi więc, CO jest w cenie,
 * ale nie ile kosztuje każdy element z osobna.
 *
 * Kwota jest tą samą, którą firma widzi na karcie zestawu — razem z jej marżą
 * i jej stawką VAT (ustawienia konta → VoltValue Projekt). Dwie różne „ceny
 * końcowe" dla jednego zestawu byłyby najgorszym możliwym błędem tego ekranu.
 *
 * ═══ LICZBY Z DANYCH BADAWCZYCH ═══
 * Retencja pojemności i liczba cykli przychodzą z backendu POLICZONE
 * (`kits.jakosc_marek`), a nie wyciągane tu wyrażeniem regularnym. LAT GWARANCJI
 * świadomie nie pokazujemy jako liczby: w danych to zdania z zastrzeżeniami
 * („2 lata w standardzie, ale po zarejestrowaniu wydłuża się do 10"), więc każda
 * wyciągnięta z nich cyfra kłamie. Gwarancja pojawia się w dokumencie wyłącznie
 * jako pełne zdanie, na stronie komponentów.
 */

// Teksty w słowniku `projekt`: cena.w_cenie.<klucz>.tytul/opis i cena.kroki.<klucz>.
const W_CENIE = ['dobor', 'zakup', 'montaz', 'gwarancja']

const KROKI = ['akceptacja', 'ogledziny', 'montaz']

/** Kwota w walucie kraju, bez groszy („28 400 zł”, „33.512 lei”). */
const kwota = (v) => `${formatPrice(Math.round(v))} ${KRAJ.symbol}`
const przecinkiem = (v) => String(v).replace('.', ',')

/** Kafelek liczby, która „zostaje na lata". Bez wartości nie renderuje się. */
const KafelekTrwalosci = ({ wartosc, opis }) => (
  <div className="rounded-xl border p-4"
       style={{ borderColor: 'var(--vv-dok-obwodka)', backgroundColor: 'var(--vv-dok-jasny)' }}>
    <p className="text-xl font-bold leading-tight" style={{ color: 'var(--vv-dok-ciemny)' }}>
      {wartosc}
    </p>
    <p className="mt-1 text-xs text-gray-600 leading-5">{opis}</p>
  </div>
)

const StronaCeny = ({
  kwotaZl, nettoZl = null, stawkaVat, kwp = null, pojemnoscMagazynuKwh = null,
  sygnalyMagazynu = null, serwisFalownikaWPolsce = false,
  // Usługi dodatkowe z ceny w karcie klienta (runda 6): same nazwy, bez kwot, jak reszta tej strony.
  uslugiDodatkowe = [],
  nazwaFirmy, numer = 5, zeStron = 5, podtytul = '',
}) => {
  const { t } = useTranslation('projekt')
  if (!kwotaZl) return null

  const naKwp = kwp ? kwotaZl / kwp : null
  const retencja = sygnalyMagazynu?.battery_retencja
  const cykle = sygnalyMagazynu?.battery_cykle

  // Kafelki powstają TYLKO z tego, co naprawdę wiemy. Pokrycie danych badawczych
  // jest nierówne — liczbę cykli zna mniejszość marek — więc sekcja bywa krótsza
  // albo znika. Dopychanie jej wymyślonymi wartościami byłoby zmyślaniem na
  // dokumencie, który firma podpisuje własną nazwą.
  // Podpisy krótkie i w naturalnym szyku. Wersja „pojemności magazynu
  // gwarantowane przez producenta na koniec okresu gwarancji" była poprawna,
  // ale nikt tak nie mówi — a pod dużą liczbą zdanie ma się czytać od razu,
  // bez wracania do początku.
  const trwalosc = [
    retencja && {
      wartosc: `${przecinkiem(Number(retencja))}%`,
      opis: t('cena.retencja_opis'),
    },
    cykle && {
      wartosc: t('cena.cykli', { liczba: Math.round(cykle).toLocaleString(KRAJ.locale) }),
      opis: t('cena.cykli_opis'),
    },
    // Serwis w Polsce to argument tylko dla polskiego klienta.
    jestPolska && serwisFalownikaWPolsce && {
      wartosc: t('cena.serwis'),
      opis: t('cena.serwis_opis'),
    },
  ].filter(Boolean)

  return (
    <StronaDokumentu numer={numer} zeStron={zeStron} nazwaFirmy={nazwaFirmy} podtytul={podtytul}>
      <NaglowekSekcji tytul={t('cena.tytul')} />

      <div className="rounded-2xl px-6 py-6 mb-7"
           style={{ backgroundColor: 'var(--vv-dok)', color: 'var(--vv-dok-na-pasku)' }}>
        <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-wide opacity-60 font-semibold">
              {t('cena.cena_koncowa')}
            </p>
            <p className="mt-1.5 text-4xl font-extrabold tabular-nums leading-none">
              {kwota(kwotaZl)}
            </p>
            {nettoZl != null && (
              <p className="mt-2 text-lg font-semibold tabular-nums opacity-90">{t('cena.netto', { kwota: kwota(nettoZl) })}</p>
            )}
            <p className="mt-2.5 text-sm opacity-70">
              {t('cena.zestaw_z_montazem')}
              {stawkaVat != null && t('cena.w_stawce_vat', { stawka: stawkaVat })}
            </p>
          </div>

          <dl className="space-y-2 text-sm">
            {kwp && (
              <div className="flex items-baseline gap-4">
                <dt className="font-bold tabular-nums w-28">{przecinkiem(kwp)} kWp</dt>
                <dd className="opacity-60">{t('cena.moc_instalacji')}</dd>
              </div>
            )}
            {pojemnoscMagazynuKwh > 0 && (
              <div className="flex items-baseline gap-4">
                <dt className="font-bold tabular-nums w-28">
                  {przecinkiem(pojemnoscMagazynuKwh)} kWh
                </dt>
                <dd className="opacity-60">{t('cena.magazyn_energii')}</dd>
              </div>
            )}
            {naKwp && (
              <div className="flex items-baseline gap-4">
                <dt className="font-bold tabular-nums w-28">{kwota(naKwp)}</dt>
                <dd className="opacity-60">{t('cena.koszt_kwp')}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      <p className="text-sm font-bold text-gray-900 mb-3">{t('cena.obejmuje')}</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-7">
        {W_CENIE.map((klucz) => (
          <div key={klucz} className="flex items-start gap-2.5 rounded-xl border p-3.5"
               style={{ borderColor: 'var(--vv-dok-obwodka)', backgroundColor: 'var(--vv-dok-jasny)' }}>
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5"
                              style={{ color: 'var(--vv-dok-ciemny)' }} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight">{t(`cena.w_cenie.${klucz}.tytul`)}</p>
              <p className="mt-0.5 text-xs text-gray-500 leading-5">{t(`cena.w_cenie.${klucz}.opis`)}</p>
            </div>
          </div>
        ))}
        {(uslugiDodatkowe || []).map((nazwa) => (
          <div key={`usluga-${nazwa}`} className="flex items-start gap-2.5 rounded-xl border p-3.5"
               style={{ borderColor: 'var(--vv-dok-obwodka)', backgroundColor: 'var(--vv-dok-jasny)' }}>
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5"
                              style={{ color: 'var(--vv-dok-ciemny)' }} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight">{nazwa}</p>
              <p className="mt-0.5 text-xs text-gray-500 leading-5">{t('cena.usluga_dodatkowa')}</p>
            </div>
          </div>
        ))}
      </div>

      {trwalosc.length > 0 && (
        <>
          <p className="text-sm font-bold text-gray-900 mb-3">
            {t('cena.na_lata')}
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mb-7">
            {trwalosc.map((t) => <KafelekTrwalosci key={t.wartosc} {...t} />)}
          </div>
        </>
      )}

      <p className="text-sm font-bold text-gray-900 mb-3">{t('cena.co_dalej')}</p>
      <ol className="grid sm:grid-cols-3 gap-4 mb-7">
        {KROKI.map((klucz, i) => (
          <li key={klucz} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold
                             flex items-center justify-center"
                  style={{ backgroundColor: 'var(--vv-dok)', color: 'var(--vv-dok-na-pasku)' }}>
              {i + 1}
            </span>
            <span className="text-xs text-gray-700 leading-5">{t(`cena.kroki.${klucz}`)}</span>
          </li>
        ))}
      </ol>

      {/* Zastrzeżenie krótko i szaro. Ściana tekstu w ramce i tak nie była czytana,
          a jej rozmiar sugerował, że dokument jest bardziej wiążący, niż jest. */}
      <p className="text-[11px] text-gray-400 leading-5">
        {t('cena.zastrzezenie')}
      </p>
    </StronaDokumentu>
  )
}

export default StronaCeny
